import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const Services = () => {
  const services = [
    {
      id: 'basic',
      name: 'Basic Detail',
      price: 80,
      duration: '1-2 hours',
      description: 'Essential cleaning and protection for your vehicle',
      features: [
        'Exterior hand wash',
        'Wheel and tire cleaning',
        'Interior vacuum',
        'Dashboard and console wipe',
        'Window cleaning (inside & out)',
        'Tire shine'
      ]
    },
    {
      id: 'premium',
      name: 'Premium Detail',
      price: 200,
      duration: '4-5 hours',
      description: 'Comprehensive detailing for a showroom finish',
      features: [
        'Everything in Basic Detail',
        'Clay bar treatment',
        'Paint sealant protection',
        'Deep interior cleaning',
        'Leather conditioning',
        'Engine bay cleaning'
      ],
      popular: true
    },
    {
      id: 'luxury',
      name: 'Luxury Detail',
      price: 700,
      duration: '1 day or more',
      description: 'Ultimate care and protection for luxury vehicles',
      features: [
        'Everything in Premium Detail',
        'Ceramic coating application (2-year warranty)',
        'Paint correction (stage 1)',
        'Premium interior protection',
        'Odor elimination treatment'
      ]
    }
  ];

  const addOns = [
    { name: 'Pet Hair Removal', price: 50 },
    { name: 'Stain Removal', price: 100 },
    { name: 'Paint Enhancement Polish', price: 200 },
    { name: 'Headlight Restoration (Light)', price: 50 },
    { name: 'Headlight Restoration (Deep)', price: 150 },
    { name: 'Headlight PPF Protection', price: 250 },
    { name: 'Engine Bay Detail', price: 50 },
    { name: 'Ceramic Coating', price: 200 },
    { name: 'Chrome Polish', price: 50 },
    { name: 'Odor Elimination (Ozone)', price: 150 }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 pt-20 relative">
      {/* Subtle Porsche background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <img 
          src="https://customer-assets.emergentagent.com/job_alinsideout-detail/artifacts/b4qlq17q_IMG_2427.jpeg"
          alt="Premium car detailing"
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-16 relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4 text-zinc-100">Services & Pricing</h1>
          <p className="text-xl text-zinc-400">Choose the perfect package for your vehicle</p>
        </div>

        {/* Service Packages */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {services.map((service) => (
            <Card 
              key={service.id}
              className={`bg-zinc-900 border-zinc-800 relative overflow-hidden transition-all duration-300 hover:border-zinc-700 hover:shadow-2xl ${
                service.popular ? 'ring-2 ring-zinc-600' : ''
              }`}
            >
              {service.popular && (
                <div className="absolute top-4 right-4 bg-zinc-100 text-zinc-950 px-3 py-1 rounded-full text-xs font-semibold">
                  MOST POPULAR
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl text-zinc-100">{service.name}</CardTitle>
                <CardDescription className="text-zinc-400">{service.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-zinc-100">€{service.price}</span>
                  <span className="text-zinc-500 ml-2">/ {service.duration}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-zinc-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/booking" state={{ selectedService: service.id }}>
                  <Button 
                    className={`w-full ${
                      service.popular 
                        ? 'bg-zinc-100 text-zinc-950 hover:bg-zinc-200' 
                        : 'bg-zinc-800 text-zinc-100 hover:bg-zinc-700'
                    } transition-all duration-300`}
                  >
                    Book {service.name}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* PPF Service */}
        <div className="mb-20">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-zinc-100 mb-3">Paint Protection Film (PPF)</h3>
                  <p className="text-zinc-400 mb-4">
                    Ultimate protection for your vehicle's paint. PPF provides invisible, self-healing protection against rock chips, scratches, and environmental damage.
                  </p>
                  <ul className="space-y-2 text-zinc-300">
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-zinc-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span>Full front coverage or custom areas</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-zinc-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span>Self-healing technology</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-zinc-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span>10-year warranty</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-5 h-5 text-zinc-400 mr-3 flex-shrink-0 mt-0.5" />
                      <span>Maintains resale value</span>
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col items-end">
                  <div className="text-3xl font-bold text-zinc-100 mb-4">Custom Quote</div>
                  <Link to="/contact">
                    <Button className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200">
                      Request Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add-Ons */}
        <div className="bg-zinc-900 rounded-lg p-8 border border-zinc-800">
          <h2 className="text-3xl font-bold mb-6 text-zinc-100">Add-On Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addOns.map((addon, index) => (
              <div 
                key={index}
                className="flex justify-between items-center p-4 bg-zinc-950 rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors"
              >
                <span className="text-zinc-300">{addon.name}</span>
                <span className="text-zinc-100 font-semibold">+${addon.price}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Service Info */}
        <div className="mt-16 grid md:grid-cols-1 gap-8">
          <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
            <h3 className="text-2xl font-bold mb-4 text-zinc-100">Service Areas</h3>
            <p className="text-zinc-400 mb-4">
              We provide mobile detailing services throughout the Algarve region and surrounding areas, specializing in premium locations.
            </p>
            <ul className="space-y-2 text-zinc-300 mb-4">
              <li>• Quinta do Lago</li>
              <li>• Vale do Lobo</li>
              <li>• Vilamoura</li>
              <li>• Surrounding Algarve areas</li>
            </ul>
            <p className="text-sm text-zinc-500">
              Our team comes fully equipped with water, power, and all necessary supplies.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
